/**
 * A non-profit's three statements on Reports (N3 of the #55 plan, §B.14):
 * the Statement of Activities, the Statement of Financial Position and Fund
 * Balances. The figures and their ties are the API's
 * (apps/api/src/services/nonprofit-statements.ts); this lays them out.
 *
 * The two classes are columns, Without Donor Restrictions and With Donor
 * Restrictions, because that is how the statements are read and filed. Each
 * statement says so when it does not tie, rather than show a total that is
 * quietly wrong.
 */

import { Link } from 'react-router-dom';
import type {
  ActivitiesRow,
  ClassColumns,
  FundBalancesReport,
  StatementOfActivities,
  StatementOfFinancialPosition,
} from '../api.js';
import { formatCents } from '../lib/money.js';
import { InfoNote } from './InfoNote.js';

function linesHref(accountId: string, report: 'income' | 'balance', window: { from?: string; to: string }): string {
  const params = new URLSearchParams({ account: accountId, report, to: window.to });
  if (window.from) params.set('from', window.from);
  return `/reports/account?${params.toString()}`;
}

/** Three money cells: Without, With, Total. */
function Cells({ columns }: { columns: ClassColumns }) {
  return (
    <>
      <td className="num">{formatCents(columns.withoutCents)}</td>
      <td className="num">{formatCents(columns.withCents)}</td>
      <td className="num">{formatCents(columns.totalCents)}</td>
    </>
  );
}

function Row({ row, from, to }: { row: ActivitiesRow; from: string; to: string }) {
  return (
    <tr>
      <td>
        <Link to={linesHref(row.accountId, 'income', { from, to })}>
          {row.code} {row.name}
        </Link>
      </td>
      <Cells columns={row} />
    </tr>
  );
}

const ClassHead = () => (
  <thead>
    <tr>
      <th />
      <th className="num">Without Donor Restrictions</th>
      <th className="num">With Donor Restrictions</th>
      <th className="num">Total</th>
    </tr>
  </thead>
);

export function ActivitiesStatement({ statement }: { statement: StatementOfActivities }) {
  const { from, to } = statement;
  return (
    <div className="report">
      <h2>Statement of Activities</h2>
      <p className="muted">
        {from} to {to}{' '}
        <InfoNote label="What The Release Line Is">
          Every expense is shown as a decrease in net assets without donor restrictions. So when a restricted fund
          spends its money, the same amount is released from With Donor Restrictions to Without, and a release through
          4800 Released From Restriction is shown the same way. Nothing extra is posted for it. How releases are
          presented is for your CPA to confirm.
        </InfoNote>
      </p>
      {!statement.tiesToFunds && (
        <p role="alert" className="error">
          These columns do not equal the funds&apos; own change in net assets for the period. Report it: a line is in a
          fund of the wrong class.
        </p>
      )}
      <div className="table-scroll">
        <table className="lines report-classes">
          <ClassHead />
          <tbody>
            <tr className="report-section">
              <th colSpan={4}>Revenue</th>
            </tr>
            {statement.revenue.map((row) => (
              <Row key={row.accountId} row={row} from={from} to={to} />
            ))}
            {statement.revenue.length === 0 && (
              <tr>
                <td className="muted" colSpan={4}>
                  No revenue posted in this period.
                </td>
              </tr>
            )}
            <tr>
              <td>Net assets released from restrictions</td>
              <Cells columns={statement.releases} />
            </tr>
            <tr className="report-total">
              <td>Total revenue and releases</td>
              <Cells
                columns={{
                  withoutCents: statement.totalRevenue.withoutCents + statement.releases.withoutCents,
                  withCents: statement.totalRevenue.withCents + statement.releases.withCents,
                  totalCents: statement.totalRevenue.totalCents + statement.releases.totalCents,
                }}
              />
            </tr>

            <tr className="report-section">
              <th colSpan={4}>Expenses</th>
            </tr>
            {statement.expenses.map((row) => (
              <Row key={row.accountId} row={row} from={from} to={to} />
            ))}
            {statement.expenses.length === 0 && (
              <tr>
                <td className="muted" colSpan={4}>
                  No expenses posted in this period.
                </td>
              </tr>
            )}
            <tr className="report-total">
              <td>Total expenses</td>
              <Cells columns={statement.totalExpenses} />
            </tr>

            <tr className="report-total is-final">
              <td>Change in net assets</td>
              <Cells columns={statement.changeInNetAssets} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FinancialPositionStatement({ statement }: { statement: StatementOfFinancialPosition }) {
  const asOf = statement.asOf;
  const account = (row: { accountId: string; code: string; name: string; balanceCents: number }) => (
    <tr key={row.accountId}>
      <td>
        <Link to={linesHref(row.accountId, 'balance', { to: asOf })}>
          {row.code} {row.name}
        </Link>
      </td>
      <td className="num">{formatCents(row.balanceCents)}</td>
    </tr>
  );
  return (
    <div className="report">
      <h2>Statement of Financial Position</h2>
      <p className="muted">As of {asOf}</p>
      {!statement.balanced && (
        <p role="alert" className="error">
          Assets do not equal liabilities plus net assets. Report it: the books are out of balance.
        </p>
      )}
      <table className="lines">
        <tbody>
          <tr className="report-section">
            <th colSpan={2}>Assets</th>
          </tr>
          {statement.assets.map(account)}
          <tr className="report-total">
            <td>Total assets</td>
            <td className="num">{formatCents(statement.totalAssetsCents)}</td>
          </tr>

          <tr className="report-section">
            <th colSpan={2}>Liabilities</th>
          </tr>
          {statement.liabilities.map(account)}
          <tr className="report-total">
            <td>Total liabilities</td>
            <td className="num">{formatCents(statement.totalLiabilitiesCents)}</td>
          </tr>

          <tr className="report-section">
            <th colSpan={2}>Net Assets</th>
          </tr>
          <tr>
            <td>Without donor restrictions</td>
            <td className="num">{formatCents(statement.netAssets.withoutCents)}</td>
          </tr>
          <tr>
            <td>With donor restrictions</td>
            <td className="num">{formatCents(statement.netAssets.withCents)}</td>
          </tr>
          <tr className="report-total">
            <td>Total net assets</td>
            <td className="num">{formatCents(statement.netAssets.totalCents)}</td>
          </tr>
          <tr className="report-total is-final">
            <td>Total liabilities and net assets</td>
            <td className="num">{formatCents(statement.totalLiabilitiesCents + statement.netAssets.totalCents)}</td>
          </tr>
        </tbody>
      </table>
      <p className="muted">
        Each class is its funds&apos; net assets, closed years and the year in progress together.
      </p>
    </div>
  );
}

const RESTRICTION: Record<string, string> = {
  none: 'Without',
  purpose: 'Purpose',
  time: 'Time',
  perpetual: 'Perpetual',
};

export function FundBalancesTable({ report }: { report: FundBalancesReport }) {
  return (
    <div className="report">
      <h2>Fund Balances</h2>
      <p className="muted">
        Balances as of {report.to}; revenue and expenses from {report.from} to {report.to}.
      </p>
      {report.funds.some((fund) => !fund.balanced) && (
        <p role="alert" className="error">
          A fund&apos;s assets do not equal its liabilities plus its net assets. Report it: the books are out of
          balance by fund.
        </p>
      )}
      <div className="table-scroll">
        <table className="lines">
          <thead>
            <tr>
              <th>Fund</th>
              <th>Restriction</th>
              <th className="num">Assets</th>
              <th className="num">Due From Other Funds</th>
              <th className="num">Liabilities</th>
              <th className="num">Net Assets</th>
              <th className="num">Revenue</th>
              <th className="num">Expenses</th>
            </tr>
          </thead>
          <tbody>
            {report.funds.map((fund) => (
              <tr key={fund.fundId}>
                <td>
                  {fund.code} {fund.name}
                  {fund.archived ? ' (Archived)' : ''}
                </td>
                <td>
                  {RESTRICTION[fund.restriction] ?? fund.restriction}
                  {fund.boardDesignated ? ', Board Designated' : ''}
                </td>
                <td className="num">{formatCents(fund.assetsCents)}</td>
                <td className="num">{formatCents(fund.dueFromOtherFundsCents)}</td>
                <td className="num">{formatCents(fund.liabilitiesCents)}</td>
                <td className="num">{formatCents(fund.netAssetsCents)}</td>
                <td className="num">{formatCents(fund.revenueCents)}</td>
                <td className="num">{formatCents(fund.expensesCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted">
        Due From Other Funds is what other funds owe this one; a negative figure is what it owes them. Across all funds
        it nets to zero.
      </p>
    </div>
  );
}
